import { useState, useEffect } from 'react'
import { UserDetailDto, VisitDto, formatTelephone, formatCoordinates } from '@ivy-monorepo/shared'
import { adminService } from '../services/admin.service'
import FormItem from './FormItem'
import Modal from 'react-modal'
import Table from './Table'
import LocationView from './locations/LocationView'
import VisitMap from './VisitMap'
import Avatar from './Avatar'

interface UserDetailPopupProps {
  userPublicId: string
  isOpen: boolean
  onClose: () => void
}

const UserDetailPopup = ({ userPublicId, isOpen, onClose }: UserDetailPopupProps) => {
  const [userDetail, setUserDetail] = useState<UserDetailDto | undefined>(undefined)
  const [selectedVisit, setSelectedVisit] = useState<string | null>(null)
  const [visitDetail, setVisitDetail] = useState<VisitDto | null>(null)

  useEffect(() => {
    if (!userPublicId || !isOpen) {
      return
    }

    const fetchUserDetail = async () => {
      try {
        const response = await adminService.getUserDetail(userPublicId)
        setUserDetail(response)
      } catch (err) {
        console.log('Failed to fetch user detail', err)
      }
    }

    fetchUserDetail()
  }, [userPublicId, isOpen])

  // Fetch visit detail when selectedVisit changes
  useEffect(() => {
    if (!selectedVisit) {
      setVisitDetail(null)
      return
    }

    const fetchVisitDetail = async () => {
      try {
        const detail = await adminService.getVisitDetail(selectedVisit)
        setVisitDetail(detail)
      } catch (err) {
        console.log('Failed to fetch visit detail', err)
        setVisitDetail(null)
      }
    }

    fetchVisitDetail()
  }, [selectedVisit])

  const handleRowClick = (visit: VisitDto) => {
    setSelectedVisit(visit.publicId)
  }

  const handleCloseVisitModal = () => {
    setSelectedVisit(null)
  }

  return (
    <Modal
      closeTimeoutMS={300}
      isOpen={isOpen}
      className="modal-popup user-detail-popup"
      overlayClassName="modal-overlay"
      onRequestClose={onClose}
    >
      {userDetail ? (
        <div className="popup-detail-container modal-content-wrapper">
          <div className="popup-profile-grid">
            <div className="popup-image-container">
              <Avatar
                firstName={userDetail?.firstName}
                lastName={userDetail?.lastName}
                url={userDetail?.imageUrl}
                className="popup-image"
              />
            </div>
            <div className="popup-info">
              <h2>
                {userDetail.firstName} {userDetail.lastName}
              </h2>
              <div className="popup-details-grid">
                <div className="popup-details-column">
                  <FormItem label="Gender Preference" value={[userDetail.gender, userDetail.preferredGenders]} type="genderPreference" className="nudge-up" />
                  <FormItem label="Email" value={userDetail.email} type="email" />
                  <FormItem label="Telephone" value={formatTelephone(userDetail.telephone)} type="telephone" />
                  <FormItem label="Date of Birth" value={userDetail.dateOfBirth} type="dateAndAge" />
                </div>
                <div className="popup-details-column">
                  <FormItem label="Device Type" value={userDetail.deviceType} />
                  <LocationView
                    latitude={userDetail.latitude}
                    longitude={userDetail.longitude}
                    renderTrigger={open => <FormItem label="Location" value={formatCoordinates(userDetail.latitude, userDetail.longitude)} onClick={open} />}
                  />
                  <FormItem label="Created" value={userDetail.createdAt} type="datetime" />
                  <FormItem label="Last Login" value={userDetail.lastLoginAt} type="datetime" />
                </div>
              </div>
            </div>
          </div>
          <div className="popup-visits-section">
            <Table
              columns={[
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Name/Address', accessor: 'venue.name', type: 'nameAndAddress' },
                { label: `Check-in`, accessor: 'checkedInAt', type: 'datetime' },
                { label: `Check-out`, accessor: 'checkedOutAt', type: 'datetime' },
                { label: `Visit duration`, accessor: 'secondsInVenue', type: 'interval' },
                { label: 'Checkout type', accessor: 'checkoutType', type: 'checkoutType' },
              ]}
              sourceData={userDetail.visits || []}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleRowClick}
              id="visits"
              name="Visits"
              keyField="publicId"
              initialSortColumn="checkedInAt"
              initialSortOrder="desc"
              disableGlobalSearch={true}
            />
          </div>
        </div>
      ) : (
        <div></div>
      )}
      <Modal
        isOpen={!!selectedVisit && !!visitDetail}
        onRequestClose={handleCloseVisitModal}
        className="visit-map-popup"
        overlayClassName="modal-overlay"
        closeTimeoutMS={300}
      >
        {visitDetail && (
          <div className="visit-map-popup-content">
            <div className="visit-map-popup-map-container">
              <VisitMap
                latitude={visitDetail.venue.latitude}
                longitude={visitDetail.venue.longitude}
                northeastLatitude={visitDetail.venue.northeastLatitude}
                northeastLongitude={visitDetail.venue.northeastLongitude}
                southwestLatitude={visitDetail.venue.southwestLatitude}
                southwestLongitude={visitDetail.venue.southwestLongitude}
                pings={visitDetail.pings || null}
              />
            </div>
          </div>
        )}
      </Modal>
    </Modal>
  )
}

export default UserDetailPopup
